/**
 * The productions page (container, D25): the list with each production's
 * studio and what it runs, the create dialog (studios, rundowns and
 * playlists to pick from) and the end confirmation. Creating and ending are
 * offered per the user's grants (`production.launch.onprem`, D14).
 */
import type { ProductionSummary } from '@acp/api-contract/productions.js';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CreateProductionDialog } from '../components/productions/CreateProductionDialog.js';
import { EndProductionDialog } from '../components/productions/EndProductionDialog.js';
import { productionHref, type ProductionRow, ProductionList } from '../components/productions/ProductionList.js';
import { useCreateProduction, useEndProduction, useGrants, usePlaylists, useProductions, useRundowns, useStudios } from '../app/hooks.js';
import { allows, allowsSomewhere } from '../lib/permissions.js';

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function sourceLabel(p: Pick<ProductionSummary, 'rundownId' | 'playlistId'>, rundowns: ReadonlyMap<string, string>, playlists: ReadonlyMap<string, string>): string {
  if (p.rundownId !== null) return `Rundown: ${rundowns.get(p.rundownId) ?? p.rundownId}`;
  if (p.playlistId !== null) return `Playlist: ${playlists.get(p.playlistId) ?? p.playlistId}`;
  return '(nothing)';
}

export function ProductionsView() {
  const productions = useProductions();
  const studios = useStudios();
  const rundowns = useRundowns();
  const playlists = usePlaylists();
  const grants = useGrants();
  const create = useCreateProduction();
  const end = useEndProduction();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [ending, setEnding] = useState<ProductionSummary | null>(null);

  const names = useMemo(
    () => ({
      studios: new Map((studios.data ?? []).map((s) => [s.studioId, s.name])),
      rundowns: new Map((rundowns.data ?? []).map((r) => [r.rundownId, r.name])),
      playlists: new Map((playlists.data ?? []).map((p) => [p.playlistId, p.name])),
    }),
    [studios.data, rundowns.data, playlists.data],
  );
  const rows: ProductionRow[] = (productions.data ?? []).map((p) => ({
    productionId: p.productionId,
    studioName: names.studios.get(p.studioId) ?? p.studioId,
    source: sourceLabel(p, names.rundowns, names.playlists),
    rehearsal: p.rehearsal,
    status: p.status,
    created: new Date(p.createdAt).toLocaleString(),
    createdBy: p.createdBy,
    endable: p.status !== 'ended' && allows(grants, 'production.launch.onprem', { siteId: p.siteId, studioId: p.studioId }),
  }));

  return (
    <div className="mx-auto grid max-w-6xl gap-6">
      <ProductionList
        productions={rows}
        loading={productions.isFetching}
        error={productions.error === null ? null : messageOf(productions.error)}
        canCreate={allowsSomewhere(grants, 'production.launch.onprem')}
        onCreate={() => {
          create.reset();
          setCreating(true);
        }}
        onRetry={() => {
          void productions.refetch();
        }}
        onOpen={(productionId) => {
          void navigate({ to: productionHref(productionId) });
        }}
        onEnd={(productionId) => {
          end.reset();
          setEnding(productions.data?.find((p) => p.productionId === productionId) ?? null);
        }}
      />
      <CreateProductionDialog
        open={creating}
        onOpenChange={setCreating}
        studios={(studios.data ?? []).map((s) => ({ id: s.studioId, name: s.name }))}
        rundowns={(rundowns.data ?? []).map((r) => ({ id: r.rundownId, name: r.name }))}
        playlists={(playlists.data ?? []).map((p) => ({ id: p.playlistId, name: p.name }))}
        pending={create.isPending}
        error={create.error === null ? null : messageOf(create.error)}
        onSubmit={(request) => {
          create.mutate(request, {
            onSuccess: (production) => {
              setCreating(false);
              toast.success('Production created');
              void navigate({ to: productionHref(production.productionId) });
            },
          });
        }}
      />
      <EndProductionDialog
        open={ending !== null}
        onOpenChange={(open) => {
          if (!open) setEnding(null);
        }}
        label={ending === null ? '' : sourceLabel(ending, names.rundowns, names.playlists)}
        active={ending?.status === 'active'}
        pending={end.isPending}
        error={end.error === null ? null : messageOf(end.error)}
        onSubmit={(reason) => {
          if (ending === null) return;
          end.mutate(
            { productionId: ending.productionId, reason },
            {
              onSuccess: () => {
                setEnding(null);
                toast.success('Production ended');
              },
            },
          );
        }}
      />
    </div>
  );
}
